import { isMatching } from "ts-pattern";

import {
  gateRetryDelayMs,
  isConcurrencyGateResponse,
  isIdempotentMethod,
  retryAfterMsForGate,
  sleep,
} from "./concurrency-gate";
import { remoteLog } from "./logging";
import { navigateTo } from "./navigation";

type FetchWithAuthOptions = {
  /**
   * If true, redirects the user to the login page if authentication fails.
   */
  redirectToLogin?: boolean;
  /**
   * Max silent retries on concurrency / DB pool gate (503). Default 1.
   * Only applied to GET/HEAD/OPTIONS.
   */
  gateRetries?: number;
};

/**
 * Wrapper around the native fetch function that handles authentication.
 * It checks for a specific error response from the server indicating that the user needs to log in.
 * If such a response is received, it redirects the user to the login page.
 *
 * FE G1: on 503 with errors concurrency_limit | db_pool_timeout, honor Retry-After
 * and silently retry once for idempotent methods (no retry storm).
 */
export async function fetchWithAuth(
  url: string,
  options: RequestInit = {},
  { redirectToLogin = true, gateRetries = 1 }: FetchWithAuthOptions = {},
): Promise<Response> {
  const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;
  const baseHeaders: Record<string, string> = {
    Accept: "application/json",
    "X-Frontend-Version": process.env.APP_VERSION ?? "",
  };
  if (!isFormData) {
    baseHeaders["Content-Type"] = "application/json";
  }

  const requestInit: RequestInit = {
    ...options,
    headers: {
      ...baseHeaders,
      ...(options.headers as Record<string, string> | undefined),
    },
    credentials: "include",
  };

  const maxAttempts = isIdempotentMethod(options.method) ? Math.max(0, gateRetries) + 1 : 1;
  let response = await fetch(url, requestInit);
  let attempts = 1;

  while (attempts < maxAttempts) {
    const maybeGateBody = await json(response);
    if (!isConcurrencyGateResponse(response.status, maybeGateBody)) {
      break;
    }
    const delayMs = gateRetryDelayMs(retryAfterMsForGate(response, maybeGateBody));
    await sleep(delayMs);
    response = await fetch(url, requestInit);
    attempts += 1;
  }

  const isPatientArea = window.location.href.includes("patient/area");

  if (response.status == 403 && redirectToLogin && !isPatientArea) {
    const maybeData = await json(response);

    if (isMatching({ message: "E_LOGIN_REQUIRED", status: "error" }, maybeData)) {
      navigateTo("/login");
      return response;
    }
  }

  const isExpectedErrorResponse = url.endsWith("/authenticate/whoami") && response.status == 403;

  if (!response.ok && !isExpectedErrorResponse) {
    const maybeData = await json(response);
    // Soft gate: do not treat as hard unexpected failure after retries.
    if (!isConcurrencyGateResponse(response.status, maybeData)) {
      const error = {
        message: `Failed loading: ${response.status} ${response.statusText}`,
        data: maybeData,
      };
      remoteLog(error, url, { remoteLog: false });
    }
  }

  return response;
}

/**
 * Helper function to parse JSON from a response.
 */
async function json(response: Response): Promise<unknown> {
  try {
    return await response.clone().json();
  } catch (_) {
    return {};
  }
}
